import React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import "./SortableTable.css";

/**
 * SortableTable Component
 *
 * @param {Array} data - Array of data objects to display
 * @param {Array} columns - Array of column configurations:
 *   {
 *     key: string,          // Key in data object
 *     label: string,        // Column header text
 *     sortable: boolean,    // Whether column is sortable
 *     render: function,     // Optional custom renderer (row, value) => ReactNode
 *     width: string,        // Optional width (e.g., '1px' for col-collapse)
 *     align: string,        // Optional alignment: 'left', 'center', 'right'
 *     className: string     // Optional additional className
 *   }
 * @param {Object} sortConfig - Current sort state { key: string, direction: 'asc' | 'desc' }
 * @param {Function} onSort - Callback when sort is triggered (columnKey)
 * @param {Function} onRowClick - Callback when row is clicked (row)
 * @param {Array} actions - Optional array of action button configs:
 *   {
 *     label: string,
 *     icon: ReactComponent,
 *     onClick: function,    // (row, event) => void
 *     className: string,    // Optional className
 *     show: function        // Optional (row) => boolean to conditionally show action
 *   }
 * @param {ReactNode} headerContent - Optional custom content in header (e.g., checkboxes)
 */
export default function SortableTable({
  data = [],
  columns = [],
  sortConfig = {},
  onSort,
  onRowClick,
  actions = [],
  headerContent,
  emptyMessage = "No data available",
}) {
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <div className="sort-icon-placeholder" />;
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUp size={16} className="sort-icon-active" />
    ) : (
      <ChevronDown size={16} className="sort-icon-active" />
    );
  };

  const handleSort = (columnKey) => {
    if (onSort) {
      onSort(columnKey);
    }
  };

  const handleRowClick = (row, event) => {
    // Don't trigger row click if clicking on an action button or checkbox
    if (
      event.target.tagName === "BUTTON" ||
      event.target.tagName === "INPUT" ||
      event.target.closest(".action-button") ||
      event.target.closest(".row-action-cell")
    ) {
      return;
    }
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const renderCell = (column, row) => {
    const value = row[column.key];

    if (column.render) {
      return column.render(row, value);
    }

    return value;
  };

  const getColumnAlign = (column) => {
    return column.align || "left";
  };

  return (
    <div className="sortable-table-wrapper">
      <div className="sortable-table-scroll">
        <table className="sortable-table">
          <thead>
            <tr>
              {headerContent && (
                <th className="sortable-table-header-content">
                  {headerContent}
                </th>
              )}
              {columns.map((column) => {
                const isSortable = column.sortable !== false && onSort;
                const cellStyle = column.width ? { width: column.width } : {};

                return (
                  <th
                    key={column.key}
                    style={cellStyle}
                    className={`sortable-table-header ${
                      isSortable ? "sortable-table-header-sortable" : ""
                    } ${column.className || ""}`}
                    onClick={() => isSortable && handleSort(column.key)}
                  >
                    <div
                      className="sortable-table-header-content"
                      style={{ justifyContent: getColumnAlign(column) }}
                    >
                      <span>{column.label}</span>
                      {isSortable && getSortIcon(column.key)}
                    </div>
                  </th>
                );
              })}
              {actions.length > 0 && (
                <th className="sortable-table-header sortable-table-actions-header">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    columns.length +
                    (actions.length > 0 ? 1 : 0) +
                    (headerContent ? 1 : 0)
                  }
                  className="sortable-table-empty"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr
                  key={row.uuid || row.id || index}
                  className={`sortable-table-row ${
                    onRowClick ? "sortable-table-row-clickable" : ""
                  }`}
                  onClick={(e) => handleRowClick(row, e)}
                >
                  {columns.map((column) => {
                    const cellStyle = {
                      textAlign: getColumnAlign(column),
                    };

                    // Apply width and maxWidth constraints if specified
                    if (column.width) {
                      cellStyle.width = column.width;
                      cellStyle.maxWidth = column.width;
                    }

                    return (
                      <td
                        key={column.key}
                        className={`sortable-table-cell ${
                          column.className || ""
                        }`}
                        style={cellStyle}
                      >
                        {renderCell(column, row)}
                      </td>
                    );
                  })}
                  {actions.length > 0 && (
                    <td className="sortable-table-cell row-action-cell">
                      <div className="sortable-table-actions">
                        {actions.map((action, actionIndex) => {
                          // Check if action should be shown
                          if (action.show && !action.show(row)) {
                            return null;
                          }

                          const Icon = action.icon;
                          return (
                            <button
                              key={actionIndex}
                              type="button"
                              className={`action-button ${
                                action.className || ""
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                action.onClick(row, e);
                              }}
                              title={action.label}
                              aria-label={action.label}
                            >
                              {Icon && <Icon size={16} />}
                              {action.label && !Icon && action.label}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
